import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Button, Message } from "semantic-ui-react";

import { PurRecord } from "react-lib/apis/PUR/purApi";
import { canUsePurDraftCreatePilot } from "react-lib/apis/PUR/purDraftCreate";
import {
  checkPurRequestOrderPermissions, createPurRequestOrder, missingPurRequestOrderPermissions, preparePurRequestOrder,
  PurRequestOrderAttempt, PurRequestOrderError, PurRequestOrderPermission, PurRequestOrderResult, purRequestOrderPermissionNames, reconcilePurRequestOrder,
} from "react-lib/apis/PUR/purRequestOrder";
import { PurClientConfig, readId } from "react-lib/apis/PUR/purRead";

import { PurchaseDraft, requestOrderInput } from "./purDrafts";
import { formatMeerpMessage } from "./purPresentation";

type Phase = "idle" | "checking" | "reviewing" | "confirming" | "saving" | "saved" | "unknown" | "reconciling";
interface State {
  phase: Phase; permission: PurRequestOrderPermission | null; errorId: string | null;
  attempt: PurRequestOrderAttempt | null; result: PurRequestOrderResult | null; poIds: number[];
}
const initial = (): State => ({ phase: "idle", permission: null, errorId: null, attempt: null, result: null, poIds: [] });
// Keep this controller in the always-mounted workspace, including while PR-01
// is visible. Unknown linked creates must not become a fresh operation on return.
export const usePurRequestOrderSave = (client: PurClientConfig, value: PurchaseDraft) => {
  const [state, setState] = useState(initial);
  const active = useRef(true), busy = useRef(false), stateRef = useRef(state);
  stateRef.current = state;
  const locked = state.phase !== "idle";
  const canReset = state.phase === "idle" || state.phase === "saved";
  useEffect(() => {
    active.current = true;
    const warn = (event: BeforeUnloadEvent) => {
      if (["saving", "unknown", "reconciling"].includes(stateRef.current.phase)) { event.preventDefault(); event.returnValue = ""; }
    };
    window.addEventListener("beforeunload", warn);
    return () => { active.current = false; window.removeEventListener("beforeunload", warn); };
  }, []);
  const check = async () => {
    if (busy.current || locked || !canUsePurDraftCreatePilot(client)) return;
    busy.current = true; setState((old) => ({ ...old, phase: "checking", errorId: null }));
    try {
      const permission = await checkPurRequestOrderPermissions(client);
      if (active.current) setState((old) => ({ ...old, phase: "idle", permission }));
    } catch { if (active.current) setState((old) => ({ ...old, phase: "idle", errorId: "MEERP_MR_PO_LEGACY_CHECK_ERROR" })); }
    finally { busy.current = false; }
  };
  const review = async () => {
    if (busy.current || locked || !state.permission || missingPurRequestOrderPermissions(state.permission).length) return;
    busy.current = true; setState((old) => ({ ...old, phase: "reviewing", errorId: null }));
    try {
      const attempt = await preparePurRequestOrder(client, requestOrderInput(value));
      if (active.current) setState((old) => ({ ...old, phase: "confirming", attempt }));
    } catch (error: unknown) {
      const id = error instanceof Error && error.message.startsWith("MEERP_") ? error.message : "MEERP_MR_PO_LEGACY_CHECK_ERROR";
      if (active.current) setState((old) => ({ ...old, phase: "idle", errorId: id }));
    } finally { busy.current = false; }
  };
  const confirm = async () => {
    if (busy.current || state.phase !== "confirming" || !state.attempt) return;
    busy.current = true; setState((old) => ({ ...old, phase: "saving", errorId: null }));
    try {
      const result = await createPurRequestOrder(client, state.attempt, () => active.current);
      if (active.current) setState((old) => ({ ...old, phase: "saved", result, poIds: [result.po.id] }));
    } catch (error: unknown) {
      const failure = error instanceof PurRequestOrderError ? error : new PurRequestOrderError("unknown", "MEERP_MR_PO_LEGACY_UNKNOWN");
      if (active.current) setState((old) => ({ ...old, phase: failure.outcome === "unknown" ? "unknown" : "idle", errorId: failure.messageId,
        poIds: failure.poIds, permission: failure.outcome === "unknown" ? old.permission : null }));
    } finally { busy.current = false; }
  };
  const reconcile = async (idText: string) => {
    const id = readId(idText);
    if (busy.current || state.phase !== "unknown" || !state.attempt || id === null) return;
    busy.current = true; setState((old) => ({ ...old, phase: "reconciling", errorId: null }));
    try {
      const result = await reconcilePurRequestOrder(client, state.attempt, id);
      if (active.current) setState((old) => ({ ...old, phase: "saved", result, poIds: [result.po.id] }));
    } catch { if (active.current) setState((old) => ({ ...old, phase: "unknown", errorId: "MEERP_MR_PO_LEGACY_UNKNOWN" })); }
    finally { busy.current = false; }
  };
  return { state, locked, canReset, check, review, confirm, reconcile,
    cancel: () => { if (!busy.current && state.phase === "confirming") setState((old) => ({ ...old, phase: "idle", attempt: null })); },
    reset: () => { if (!busy.current && canReset) setState(initial()); } };
};
export type PurRequestOrderSaveController = ReturnType<typeof usePurRequestOrderSave>;

const PurRequestOrderSave: React.FC<{ client: PurClientConfig; controller: PurRequestOrderSaveController; onOpenSaved?: (record: PurRecord) => void }> = ({ client, controller, onOpenSaved }) => {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) => formatMeerpMessage(intl, id, values);
  const { state } = controller;
  const [accepted, setAccepted] = useState(false), [candidateId, setCandidateId] = useState("");
  useEffect(() => { setAccepted(false); }, [state.attempt, state.phase]);
  const eligible = canUsePurDraftCreatePilot(client);
  const missing = state.permission ? missingPurRequestOrderPermissions(state.permission, state.errorId === "MEERP_MR_PO_LEGACY_ORDER_PERMISSION") : [];
  const fieldLabel = { quantity: "MEERP_ORDERED_QUANTITY", unitCost: "MEERP_UNIT_COST", factor: "MEERP_CONVERSION_FACTOR" };
  return <section className="card-meerp__draft-save" aria-label={t("MEERP_MR_PO_LEGACY_SECTION")}>
    <Message warning><p>{t("MEERP_MR_PO_LEGACY_WARNING")}</p><p>{t("MEERP_MR_PO_LEGACY_IGNORED")}</p></Message>
    {!eligible && <Message warning>{t("MEERP_MR_PO_LEGACY_SCOPE")}</Message>}
    <Button type="button" disabled={!eligible || controller.locked} onClick={controller.check}>{t("MEERP_MR_PO_LEGACY_CHECK")}</Button>
    {state.permission && !controller.locked && <Message positive={!missing.length} warning={Boolean(missing.length)} role="status">
      <p>{t(missing.length ? "MEERP_MR_PO_LEGACY_PERMISSION" : "MEERP_MR_PO_LEGACY_READY")}</p>
      {missing.length > 0 && <ul>{missing.map((name) => <li key={name}><code>{name}</code></li>)}</ul>}
    </Message>}
    {state.errorId && <Message negative role="alert">{t(state.errorId)}{state.errorId === "MEERP_MR_PO_LEGACY_ORDER_PERMISSION" && <p><code>{purRequestOrderPermissionNames.order}</code></p>}</Message>}
    <Button type="button" primary disabled={!eligible || controller.locked || !state.permission || missing.length > 0} onClick={controller.review}>{t("MEERP_MR_PO_LINKED_SAVE")}</Button>
    {["checking", "reviewing", "saving"].includes(state.phase) && <p role="status">{t("MEERP_MR_PO_LEGACY_LOADING")}</p>}
    {state.phase === "confirming" && state.attempt && <Message warning role="group" aria-label={t("MEERP_MR_PO_LEGACY_CONFIRM_TITLE")}>
      <p>{t("MEERP_MR_PO_LEGACY_CONFIRM_NOTE", { number: state.attempt.source.number, id: state.attempt.source.id, actor: state.attempt.actorUserId })}</p>
      <p>{t("MEERP_MR_PO_LEGACY_WARNING")}</p><p>{t("MEERP_MR_PO_LEGACY_IGNORED")}</p>
      <div className="card-meerp__table-wrap"><table className="card-meerp__table"><thead><tr>
        {["MEERP_MR_PO_SOURCE_ITEM", "MEERP_MR_PO_FORM_QUANTITY", "MEERP_MR_PO_API_QUANTITY", "MEERP_MR_PO_FORM_COST", "MEERP_MR_PO_API_COST", "MEERP_MR_PO_FORM_FACTOR", "MEERP_MR_PO_API_FACTOR"].map((id) => <th key={id}>{t(id)}</th>)}
      </tr></thead><tbody>{state.attempt.input.lines.map((line) => <tr key={line.requestItemId}>
        <td>{line.requestItemId}</td><td>{line.quantity}</td><td>{line.remaining}</td><td>{line.unitCost || "—"}</td><td>{t("MEERP_MR_PO_MASTER_COST")}</td><td>{line.factor}</td><td>1.0000</td>
      </tr>)}</tbody></table></div>
      <p>{t("MEERP_MR_PO_LEGACY_MR_UPDATE")}</p>
      <label><input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} /> {t("MEERP_MR_PO_LEGACY_ACCEPT")}</label>
      <div className="card-meerp__toolbar"><Button type="button" disabled={!accepted} onClick={() => { if (accepted) controller.confirm(); }}>{t("MEERP_MR_PO_LEGACY_CONFIRM")}</Button>
        <Button type="button" onClick={controller.cancel}>{t("MEERP_DRAFT_RESET_CANCEL")}</Button></div>
    </Message>}
    {state.phase === "saved" && state.result && <Message warning role="status">
      <p>{t("MEERP_MR_PO_LEGACY_SAVED", { number: state.result.po.number, id: state.result.po.id, mr: state.result.mr.number })}</p>
      <p>{t("MEERP_MR_PO_LEGACY_RESULT_NOTE")}</p>
      <p>{t("MEERP_STATUS")}: {state.result.po.status} · MR: {state.result.mr.status} · {t("MEERP_TOTAL")}: {state.result.po.totalAmount ?? "—"}</p>
      <div className="card-meerp__table-wrap"><table className="card-meerp__table"><thead><tr>
        {["MEERP_PO_LINE_ID", "MEERP_PRODUCT", "MEERP_ORDERED_QUANTITY", "MEERP_UNIT_COST", "MEERP_UNIT", "MEERP_CONVERSION_FACTOR"].map((id) => <th key={id}>{t(id)}</th>)}
      </tr></thead><tbody>{state.result.po.items.map((line) => line.kind === "purchaseOrders" && <tr key={line.id}>
        <td>{line.id}</td><td>{line.productName || `#${line.productId}`}</td><td>{line.orderedQuantity}</td><td>{line.unitCost}</td><td>{line.unitName || `#${line.unitId}`}</td><td>{line.conversionFactor}</td>
      </tr>)}</tbody></table></div>
      {state.result.differences.length > 0 && <ul>{state.result.differences.map((diff) => <li key={`${diff.requestItemId}:${diff.field}`}>
        {t("MEERP_MR_PO_LEGACY_DIFFERENCE", { id: diff.requestItemId, field: t(fieldLabel[diff.field]), form: diff.form, actual: diff.actual })}
      </li>)}</ul>}
      {onOpenSaved && <Button type="button" onClick={() => state.result && onOpenSaved(state.result.po)}>{t("MEERP_MR_PO_LEGACY_OPEN")}</Button>}
    </Message>}
    {["unknown", "reconciling"].includes(state.phase) && <Message warning>
      <p>{t("MEERP_MR_PO_LEGACY_RECOVERY")}</p>
      {state.poIds.length > 0 && <p>{t("MEERP_MR_PO_LEGACY_IDS", { ids: state.poIds.join(", ") })}</p>}
      <label htmlFor="mr-po-reconcile-id">{t("MEERP_MR_PO_LEGACY_CANDIDATE")}</label>
      <input id="mr-po-reconcile-id" inputMode="numeric" value={state.poIds.length === 1 ? String(state.poIds[0]) : candidateId}
        disabled={state.phase === "reconciling" || state.poIds.length === 1} onChange={(event) => setCandidateId(event.target.value)} />
      <Button type="button" disabled={state.phase === "reconciling" || readId(state.poIds.length === 1 ? state.poIds[0] : candidateId) === null}
        onClick={() => controller.reconcile(state.poIds.length === 1 ? String(state.poIds[0]) : candidateId)}>{t("MEERP_MR_PO_LEGACY_RECONCILE")}</Button>
    </Message>}
  </section>;
};
export default PurRequestOrderSave;
